import React, { useMemo, useRef } from "react";
import { clampVisibleRange } from "./displayDensity";

type ChartNavigatorProps = {
  pointCount: number;
  visibleRange: [number, number];
  values: Array<number | null>;
  presets: readonly string[];
  activePreset: string | null;
  onChange: (range: [number, number]) => void;
  onSelectPreset: (preset: string) => void;
};

function presetAccessibleName(preset: string): string {
  switch (preset) {
    case "1D": return "Show 1 day";
    case "1W": return "Show 1 week";
    case "1M": return "Show 1 month";
    case "3M": return "Show 3 months";
    case "6M": return "Show 6 months";
    case "1Y": return "Show 1 year";
    case "2Y": return "Show 2 years";
    case "5Y": return "Show 5 years";
    case "ALL": return "Show all data";
    default: return `Show ${preset}`;
  }
}

type DragKind = "move" | "start" | "end";

function sparklinePath(values: Array<number | null>, width: number, height: number): string {
  const numbers = values.filter((value): value is number => Number.isFinite(value));
  if (numbers.length === 0 || width <= 0 || height <= 0) return "";
  const min = Math.min(...numbers);
  const max = Math.max(...numbers);
  const range = Math.max(max - min, Number.EPSILON);
  const last = Math.max(values.length - 1, 1);
  const points = values.flatMap((value, index) => {
    if (!Number.isFinite(value)) return [];
    const x = (index / last) * width;
    const y = height - ((Number(value) - min) / range) * height;
    return [`${x.toFixed(2)},${y.toFixed(2)}`];
  });
  return points.length ? `M ${points.join(" L ")}` : "";
}

export default function ChartNavigator({
  pointCount,
  visibleRange,
  values,
  presets,
  activePreset,
  onChange,
  onSelectPreset,
}: ChartNavigatorProps): React.ReactElement {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ kind: DragKind; originX: number; start: number; end: number } | null>(null);
  const lastIndex = Math.max(0, pointCount - 1);
  const [start, end] = visibleRange;
  const leftPct = lastIndex === 0 ? 0 : (start / lastIndex) * 100;
  const widthPct = lastIndex === 0 ? 100 : ((end - start) / lastIndex) * 100;
  const path = useMemo(() => sparklinePath(values, 100, 28), [values]);

  const moveHandle = (kind: 'start' | 'end', direction: -1 | 1, coarse: boolean) => {
    const step = coarse ? Math.max(1, Math.round(pointCount * 0.05)) : 1;
    const nextStart = kind === 'start' ? start + (direction * step) : start;
    const nextEnd = kind === 'end' ? end + (direction * step) : end;
    onChange(clampVisibleRange(nextStart, nextEnd, lastIndex));
  };

  const handleKeyboard = (kind: 'start' | 'end', event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    moveHandle(kind, event.key === 'ArrowLeft' ? -1 : 1, event.shiftKey);
  };

  const rangeFromClientX = (clientX: number, kind: DragKind, origin: { start: number; end: number; originX: number }) => {
    const track = trackRef.current;
    if (!track) return origin;
    const bounds = track.getBoundingClientRect();
    if (bounds.width <= 0) return origin;
    const indexAt = (x: number) => Math.round(((x - bounds.left) / bounds.width) * lastIndex);
    if (kind === "start") return { start: indexAt(clientX), end: origin.end };
    if (kind === "end") return { start: origin.start, end: indexAt(clientX) };
    const delta = indexAt(clientX) - indexAt(origin.originX);
    return { start: origin.start + delta, end: origin.end + delta };
  };

  const commit = (kind: DragKind, clientX: number, origin: { start: number; end: number; originX: number }) => {
    const next = rangeFromClientX(clientX, kind, origin);
    onChange(clampVisibleRange(next.start, next.end, lastIndex));
  };

  const beginDrag = (kind: DragKind, event: React.PointerEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const origin = { kind, originX: event.clientX, start, end };
    dragRef.current = origin;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  return (
    <div
      className="unified-chart__navigator"
      data-testid="chart-navigator"
      data-visible-start={start}
      data-visible-end={end}
      data-source-point-count={pointCount}
      aria-label="Visible chart range"
    >
      <div className="unified-chart__navigator-header">
        <strong>Range</strong>
        <span>Drag the window or use the handles</span>
      </div>
      <div
        ref={trackRef}
        className="unified-chart__navigator-track"
        data-testid="chart-navigator-track"
        onPointerDown={(event) => {
          if (event.target !== event.currentTarget) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          const ratio = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
          const center = Math.round(ratio * lastIndex);
          const span = end - start;
          onChange(clampVisibleRange(center - Math.floor(span / 2), center - Math.floor(span / 2) + span, lastIndex));
        }}
      >
        <svg className="unified-chart__navigator-spark" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden>
          {path ? <path d={path} /> : null}
        </svg>
        <div
          className="unified-chart__navigator-window"
          data-testid="chart-navigator-window"
          style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 1.5)}%` }}
          onPointerDown={(event) => beginDrag("move", event)}
          onPointerMove={(event) => {
            if (!dragRef.current || dragRef.current.kind !== "move") return;
            commit("move", event.clientX, dragRef.current);
          }}
          onPointerUp={() => {
            dragRef.current = null;
          }}
        >
          <div
            role="slider"
            tabIndex={0}
            className="unified-chart__navigator-handle"
            aria-label="Range start"
            aria-valuemin={0}
            aria-valuemax={Math.max(0, end - 1)}
            aria-valuenow={start}
            data-testid="chart-navigator-start"
            onKeyDown={(event) => handleKeyboard('start', event)}
            onPointerDown={(event) => beginDrag("start", event)}
            onPointerMove={(event) => {
              if (!dragRef.current || dragRef.current.kind !== "start") return;
              commit("start", event.clientX, dragRef.current);
            }}
            onPointerUp={() => {
              dragRef.current = null;
            }}
          />
          <div
            role="slider"
            tabIndex={0}
            className="unified-chart__navigator-handle unified-chart__navigator-handle--end"
            aria-label="Range end"
            aria-valuemin={Math.min(lastIndex, start + 1)}
            aria-valuemax={lastIndex}
            aria-valuenow={end}
            data-testid="chart-navigator-end"
            onKeyDown={(event) => handleKeyboard('end', event)}
            onPointerDown={(event) => beginDrag("end", event)}
            onPointerMove={(event) => {
              if (!dragRef.current || dragRef.current.kind !== "end") return;
              commit("end", event.clientX, dragRef.current);
            }}
            onPointerUp={() => {
              dragRef.current = null;
            }}
          />
        </div>
      </div>
      <div className="unified-chart__range-presets" role="group" aria-label="Chart range presets">
        {presets.map((preset) => (
          <button
            key={preset}
            type="button"
            className={activePreset === preset ? "is-active" : ""}
            aria-label={presetAccessibleName(preset)}
            aria-pressed={activePreset === preset}
            onClick={() => onSelectPreset(preset)}
          >
            {preset === "ALL" ? "All" : preset}
          </button>
        ))}
      </div>
    </div>
  );
}
